"use client";

import { useResolvedTheme } from "@/lib/hooks/use-resolved-theme";
import { ConstellationBackground } from "./constellation-background";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

{/* Fixed background layer for the entire home page */ }
export function HomeBackground() {
  const { isLight, mounted } = useResolvedTheme();
  const { scrollYProgress } = useScroll();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Subtly move background layers at different speeds for depth
  // On mobile these transforms are ignored (we render static divs instead)
  const constellationY = useTransform(scrollYProgress, [0, 1], ["0%", "5%"]);
  const textureY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      {/* Base Radial Gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: isLight
            ? "radial-gradient(circle at 50% 50%, #ffffff 0%, #d4d4d8 100%)"
            : "radial-gradient(circle at 50% 50%, #18181b 0%, #09090b 100%)",
        }}
      />

      {/* Accent Glow — reduced blur on mobile for GPU savings */}
      <div
        className={`absolute inset-0 opacity-30 pointer-events-none ${isMobile ? "blur-[40px]" : "blur-[100px]"}`}
        style={{
          background: isLight
            ? "radial-gradient(circle at 20% 20%, rgba(249, 115, 22, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(249, 115, 22, 0.05) 0%, transparent 50%)"
            : "radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)",
        }}
      />

      {/* Texture Overlay — static on mobile, parallax on desktop */}
      {isMobile ? (
        <div
          className={`absolute inset-0 pointer-events-none ${isLight
            ? "opacity-[0.08] mix-blend-multiply"
            : "opacity-[0.12] mix-blend-overlay"
            }`}
          style={{
            backgroundImage: "url('/media/images/backgrounds/noname-opt.webp')",
            backgroundSize: "300px",
            backgroundRepeat: "repeat",
          }}
        />
      ) : (
        <motion.div
          className={`absolute inset-0 pointer-events-none ${isLight
            ? "opacity-[0.08] mix-blend-multiply"
            : "opacity-[0.12] mix-blend-overlay"
            }`}
          style={{
            backgroundImage: "url('/media/images/backgrounds/noname-opt.webp')",
            backgroundSize: "300px",
            backgroundRepeat: "repeat",
            y: textureY
          }}
        />
      )}

      {/* Constellation — static on mobile, parallax on desktop */}
      {isMobile ? (
        <div className="absolute inset-0">
          <ConstellationBackground isLight={isLight} />
        </div>
      ) : (
        <motion.div
          className="absolute inset-0"
          style={{ y: constellationY }}
        >
          <ConstellationBackground isLight={isLight} />
        </motion.div>
      )}

      {/* Darkening Overlay */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-700 ${isLight
          ? "bg-linear-to-b from-white/30 via-transparent to-white/60"
          : "bg-linear-to-b from-black/40 via-transparent to-black/90"
          }`}
      />
    </div>
  );
}
